import type { TypedDocumentNode } from '@apollo/client';

import { gengql } from '@/__generated__';
import {
  ActionUpdateExpertRequestCandidateMutationVariables,
  ExpertRequestState,
  ExpertRequestType,
} from '@/__generated__/graphql';
import { fetchProject } from '@/actions/project';
import { AppThunk } from '@/store';
import { shouldResetCollection } from '@/utils';

import { Candidate, ExpertRequest } from '..';
import {
  ADD_EXPERT_REQUEST_CANDIDATES,
  CREATE_EXPERT_REQUEST,
  GET_EXPERT_REQUEST,
  GET_EXPERT_REQUEST_CANDIDATES,
  REQUEST_ADD_EXPERT_REQUEST_CANDIDATE,
  UPDATE_EXPERT_REQUEST,
  UPDATE_EXPERT_REQUEST_CANDIDATE,
} from './queries';

export const EXPERT_REQUEST__UPDATE = 'EXPERT_REQUEST__UPDATE';
export const EXPERT_REQUEST__DELETE = 'EXPERT_REQUEST__DELETE';
export const EXPERT_REQUEST__ADD_CANDIDATE = 'EXPERT_REQUEST__ADD_CANDIDATE';
export const EXPERT_REQUEST__FETCHED_INIT_CANDIDATES = 'EXPERT_REQUEST__FETCHED_INIT_CANDIDATES';
export const EXPERT_REQUEST__FETCHED_MORE_CANDIDATES = 'EXPERT_REQUEST__FETCHED_MORE_CANDIDATES';
export const EXPERT_REQUEST__FETCHING_CANDIDATES = 'EXPERT_REQUEST__FETCHING_CANDIDATES';
export const EXPERT_REQUEST__REMOVE_CANDIDATE = 'EXPERT_REQUEST__REMOVE_CANDIDATE';
export const EXPERT_REQUEST__UPDATE_CANDIDATE = 'EXPERT_REQUEST__UPDATE_CANDIDATE';
export const EXPERT_REQUEST__LIST = 'EXPERT_REQUEST__LIST';
export const EXPERT_REQUEST__LIST_LOADING = 'EXPERT_REQUEST__LIST_LOADING';
export const EXPERT_REQUEST__LIST_LOADED = 'EXPERT_REQUEST__LIST_LOADED';

export const ER_TYPE_LABELS = Object.freeze({
  [ExpertRequestType.Consultation]: 'Consultation',
  [ExpertRequestType.NewHire]: 'New Hire',
  [ExpertRequestType.ConsultingProject]: 'Consulting Project',
  [ExpertRequestType.WrittenReview]: 'Written Review',
  [ExpertRequestType.ProBonoConsultation]: 'Pro Bono', // legacy
});

// ER types we no longer let customers create (ENG-5265). This is a phase-1,
// frontend-only deprecation: we stop accepting NEW requests of these types but
// keep the backend types intact so historical ERs still render — and so we can
// re-enable them later without a data migration. Labels above are retained for
// that historical display; only creation/edit surfaces should consult this list.
export const DEPRECATED_ER_TYPES: readonly ExpertRequestType[] = [
  ExpertRequestType.NewHire,
  ExpertRequestType.ProBonoConsultation,
];

export const isDeprecatedType = (type: ExpertRequestType | null | undefined): boolean =>
  !!type && DEPRECATED_ER_TYPES.includes(type);

export const isWorkType = (type: ExpertRequestType): boolean =>
  [ExpertRequestType.NewHire, ExpertRequestType.ConsultingProject].includes(type);

export const isUnpaidType = (type: ExpertRequestType): boolean =>
  [ExpertRequestType.NewHire, ExpertRequestType.ConsultingProject].includes(type);

export const isAttachmentDeliverableType = (type: ExpertRequestType): boolean =>
  [ExpertRequestType.WrittenReview].includes(type);

export const isOpportunityType = (type: ExpertRequestType): boolean =>
  [ExpertRequestType.ConsultingProject, ExpertRequestType.NewHire].includes(type);

export const isCallType = (type: ExpertRequestType): boolean =>
  [
    ExpertRequestType.Consultation,
    ExpertRequestType.ConsultingProject,
    ExpertRequestType.NewHire,
  ].includes(type);

export const isFixedRate = (type: ExpertRequestType): boolean => {
  return [
    ExpertRequestType.ConsultingProject,
    ExpertRequestType.NewHire,
    ExpertRequestType.WrittenReview,
  ].includes(type);
};

export const isSetExpectedDurationType = (type: ExpertRequestType): boolean =>
  [ExpertRequestType.WrittenReview].includes(type);

export const candidateCategories = {
  matched: [
    'polishing',
    'vetting',
    'verified',
    'matched',
    'rejected_by_client',
    'rejected_by_research',
  ],
  suggested: [
    'suggested_by_platform',
    'suggested_by_research',
    'contacted',
    'interested',
    'rejected_suggestion',
  ],
};

export function saveExpertRequest(
  expertRequest: any,
  {
    fetchProject = false,
    includeQueries = true,
    includeAttachments = true,
    includeAdminFields = true,
  } = {}
): AppThunk<Promise<any>> {
  return async (dispatch, _getState, { graphql }) => {
    const isUpdate = expertRequest.id;
    const mutationName = isUpdate ? 'updateExpertRequest' : 'createExpertRequest';
    const mutation = isUpdate ? UPDATE_EXPERT_REQUEST : CREATE_EXPERT_REQUEST;

    const params = {
      id: expertRequest.id,
      name: expertRequest.name,
      includeProject: fetchProject,
      description: expertRequest.description?.trim(),
      disclosure: expertRequest.disclosure,
      focus_areas: expertRequest.focus_areas,
      project_id: expertRequest.project_id,
      phone: expertRequest.phone,
      companies_avoid: expertRequest.companies_avoid.filter((x: any) => x && x.trim()),
      companies_pursue: expertRequest.companies_pursue.filter((x: any) => x?.trim()),
      regions: expertRequest.regions.map((x: any) => x.id),
      sectors: expertRequest.sectors.map((x: any) => x.id),
      er_type: expertRequest.er_type,
      stage: expertRequest.stage,
      instructions_research: expertRequest.instructions_research?.trim(),
      group_about: expertRequest.group_about?.trim(),
      job_scope: expertRequest.job_scope?.trim(),
      expected_duration: expertRequest.expected_duration,
      opportunity_location: expertRequest.opportunity_location,
    };

    if (includeAdminFields) {
      // @ts-expect-error TS(2339): Property 'tags' does not exist on type '{ id: any;... Remove this comment to see the full error message
      params.tags = expertRequest.tags;
      // @ts-expect-error TS(2339): Property 'time_done_scoping_call' does not exist o... Remove this comment to see the full error message
      params.time_done_scoping_call = expertRequest.time_done_scoping_call
        ? expertRequest.time_done_scoping_call
        : '0001-01-01T00:00:00Z';
      // @ts-expect-error TS(2339): Property 'origin' does not exist on type '{ id: any;...
      params.origin = expertRequest.origin;
      // @ts-expect-error TS(2339): Property 'sourcing_scope' does not exist on type '{ id: any;...
      params.sourcing_scope = expertRequest.sourcing_scope;
      // @ts-expect-error TS(2339): Property 'allow_internal_vetting' does not exist on type '{ id: any;...
      params.allow_internal_vetting = expertRequest.allow_internal_vetting;
      // @ts-expect-error TS(2339): Property 'allow_external_recruiting' does not exist on type '{ id: any;...
      params.allow_external_recruiting = expertRequest.allow_external_recruiting;
    }

    // they share same permission
    if (includeQueries) {
      // @ts-expect-error TS(2339): Property 'qualifications' does not exist on type '... Remove this comment to see the full error message
      params.qualifications = expertRequest.qualifications?.filter((q: any) => q?.query?.trim());
      // @ts-expect-error TS(2339): Property 'questions' does not exist on type '{ id:... Remove this comment to see the full error message
      params.questions = expertRequest.questions?.filter((q: any) => q?.query?.trim());
    }

    const data = await graphql.mutate(
      mutation as unknown as TypedDocumentNode<Record<string, any>>,
      params
    );
    const updatedExpertRequest = { ...expertRequest, ...data[mutationName] };

    if (includeAttachments) {
      const attachments = expertRequest.attachments || [];
      const newAttachments = await dispatch(
        updateAttachments(
          updatedExpertRequest.id,
          attachments
            .filter((a: any) => !('id' in a))
            .map((a: any) => {
              return {
                name: a.name,
                description: a.description,
                file_url: a.file_url,
                hide_from_experts: a.hide_from_experts,
              };
            })
        )
      );
      const existingAttachments = attachments.filter((a: any) => 'id' in a);
      updatedExpertRequest.attachments = [...existingAttachments, ...newAttachments];
    }

    dispatch({
      type: EXPERT_REQUEST__UPDATE,
      expertRequest: updatedExpertRequest,
    });

    if (isUpdate) {
      dispatch(fetchExpertRequestCandidates(updatedExpertRequest.id, 'suggested', true));
    }

    return updatedExpertRequest;
  };
}

function updateAttachments(expertRequestId: string, attachments: any): AppThunk<Promise<any>> {
  return async (_dispatch, _getState, { graphql }) => {
    const newAttachments = attachments
      .filter((a: any) => !('id' in a))
      .map((a: any) => {
        return {
          name: a.name,
          description: a.description,
          file_url: a.file_url,
          hide_from_experts: a.hide_from_experts,
        };
      });
    const results = await graphql.mutate(
      gengql(/* GraphQL */ `
        mutation actionUpdateAttachments(
          $expertRequestId: String!
          $attachments: [EngagementAttachmentInput]!
        ) {
          requestAddAttachments(expert_request_id: $expertRequestId, attachments: $attachments) {
            id
            created_at
            author {
              id
              name
              html_url
            }
            expert_request_id
            consultation_id
            name
            description
            file_url
            hide_from_experts
          }
        }
      `),
      {
        expertRequestId,
        attachments: newAttachments,
      }
    );
    return results.requestAddAttachments;
  };
}

// Minimal "move to another project" (ENG-5314). Sends ONLY id + project_id; the
// api resolver reloads the full ER and overlays just project_id (no clobber).
// A move into a different team is refused server-side (ENG-5366) — the picker in
// EditAssociatedProjectDialog only ever offers same-team projects, so this call
// should never actually attempt a cross-team move in practice. Do NOT route this
// through saveExpertRequest, which submits the whole ER and would wipe fields
// from a partial list object.
export function moveExpertRequest(
  id: string,
  projectId: string,
  sourceProjectId?: string
): AppThunk<Promise<any>> {
  return async (dispatch, _getState, { graphql }) => {
    const data = await graphql.mutate(
      gengql(/* GraphQL */ `
        mutation actionMoveExpertRequest($id: String!, $projectId: String!) {
          updateExpertRequest(id: $id, project_id: $projectId) {
            id
            project {
              id
              name
            }
          }
        }
      `),
      { id, projectId }
    );

    // A null payload (no thrown GraphQL error) still means the move didn't take —
    // throw so the dialog's catch surfaces an error instead of closing as success.
    if (!data?.updateExpertRequest) {
      throw new Error('Failed to move expert request: no updateExpertRequest in response.');
    }

    // Surgically drop the moved ER from the source project's list so the
    // currently-mounted project page updates in place. We deliberately do NOT
    // invalidate the whole projects cache: that resets every collection to
    // empty, blanking the mounted source page (ProjectDetails renders nothing
    // when its edge is gone) with no automatic refetch.
    if (sourceProjectId) {
      dispatch({
        type: EXPERT_REQUEST__DELETE,
        projectId: sourceProjectId,
        id,
      });
    }

    // Merge the moved ER into the destination project's edge, for pages that
    // read from state.projects (ProjectDetails). fetchProject dispatches
    // PROJECT__UPDATE, which merges one edge and never resets a collection.
    await dispatch(fetchProject(projectId));

    // /expert_requests reads from its own Apollo-cached searchProjects query,
    // not state.projects — the dispatches above are invisible to it. Refetch
    // so a move made from that page's dialog is reflected without a manual
    // reload. A no-op if searchProjects isn't currently mounted (e.g. when
    // called from ProjectDetails).
    // Swallowed deliberately: the move already committed, so a failed refetch is
    // a stale list, not a failed operation. RefetchQueriesResult is a Promise, so
    // without this a rejection escapes as an unhandled rejection.
    graphql.client.refetchQueries({ include: ['searchProjects'] }).catch(() => {});

    return data.updateExpertRequest;
  };
}

export function fetchExpertRequest(id: string): AppThunk<Promise<ExpertRequest>> {
  return async (dispatch, _getState, { graphql }) => {
    const result = await graphql.query(GET_EXPERT_REQUEST, { expertRequestId: id });
    const { expertRequest } = result;
    dispatch({
      type: EXPERT_REQUEST__UPDATE,
      expertRequest,
    });
    return expertRequest;
  };
}

export function fetchExpertRequestCandidates(
  id: string,
  type: 'matched' | 'suggested',
  reset?: boolean,
  cursor?: string,
  pageSize = 10
): AppThunk<Promise<void>> {
  const states = candidateCategories[type];
  return async (dispatch, _getState, { graphql }) => {
    dispatch({
      type: EXPERT_REQUEST__FETCHING_CANDIDATES,
      expertRequestId: id,
    });
    const result = await graphql.query(GET_EXPERT_REQUEST_CANDIDATES, {
      expertRequestId: id,
      states,
      first: pageSize,
      after: cursor,
    });
    const { expertRequestCandidates } = result;

    dispatch({
      type: reset
        ? EXPERT_REQUEST__FETCHED_INIT_CANDIDATES
        : EXPERT_REQUEST__FETCHED_MORE_CANDIDATES,
      expertRequestId: id,
      candidates: expertRequestCandidates,
      candidateType: type,
    });
  };
}

export function fetchExpertRequests({
  groupId,
  memberOnly,
  withGroupOnly = true,
  state,
  collection = state || 'all',
}: any = {}): AppThunk<Promise<any>> {
  return async (dispatch, getState, { graphql }) => {
    const expertRequests = getState().expertRequests[collection];
    if (!shouldResetCollection(expertRequests, 1000)) return expertRequests;

    const { viewer } = getState();

    const data = await graphql.query(
      gengql(/* GraphQL */ `
        query actionFetchExpertRequests(
          $memberOnly: Boolean
          $state: ExpertRequestState
          $groupId: String
          $withGroupOnly: Boolean
        ) {
          expertRequests(
            first: 200
            member_only: $memberOnly
            with_group_only: $withGroupOnly
            state: $state
            group_id: $groupId
          ) {
            pageInfo {
              hasNextPage
            }
            edges {
              node {
                id
                html_url
                name
                state
                disclosure
                er_type
                project {
                  id
                  group {
                    id
                    name
                    account_type
                  }
                }
              }
            }
          }
        }
      `),
      {
        groupId,
        memberOnly: memberOnly || !viewer.admin,
        withGroupOnly,
        state,
      }
    );

    const page = data.expertRequests;

    dispatch({
      type: EXPERT_REQUEST__LIST,
      collection,
      reset: true,
      ...page,
    });

    return page;
  };
}

export function fetchExpertRequestMembers(id: string): AppThunk<Promise<any>> {
  return async (dispatch, _getState, { graphql }) => {
    const result = await graphql.query(
      gengql(/* GraphQL */ `
        query actionFetchExpertRequestMembers($id: String!) {
          expertRequest(id: $id) {
            id
            er_type
            name
            permissions
            disclosure
            project {
              id
              tracking_code
              group {
                id
                name
              }
              members {
                id
                role
                state
                user {
                  id
                  username
                  name
                }
              }
            }
          }
        }
      `),
      { id }
    );

    const { expertRequest } = result;

    if (expertRequest) {
      dispatch({
        type: EXPERT_REQUEST__UPDATE,
        expertRequest,
      });
    }
    return expertRequest;
  };
}

export function fetchProfileCandidates(id: string): AppThunk<Promise<any>> {
  return async (_dispatch, _getState, { graphql }) => {
    const result = await graphql.query(
      gengql(/* GraphQL */ `
        query actionFetchProfileCandidates($id: String!) {
          profileCandidates(id: $id) {
            id
            request_id
          }
        }
      `),
      { id }
    );

    return result.profileCandidates;
  };
}

export function fetchCandidate(expertRequestId: string, profileId: string): AppThunk<Promise<any>> {
  return async (_dispatch, _getState, { graphql }) => {
    const result = await graphql.query(
      gengql(/* GraphQL */ `
        query actionFetchCandidate($expertRequestId: String!, $profileId: String!) {
          profileCandidate(profile_id: $profileId, expert_request_id: $expertRequestId) {
            id
            credit_rate
            show_engagement_agreement
          }
        }
      `),
      { expertRequestId, profileId }
    );

    return result.profileCandidate;
  };
}

export function addExpertRequestCandidates({
  expertRequestId,
  dryRun = true,
  sendInvitationEmail,
  invitationEmailSubject,
  invitationEmailBody,
  profileIds,
  state,
}: any): AppThunk<Promise<any>> {
  return async (dispatch, _getState, { graphql }) => {
    const result = await graphql.mutate(ADD_EXPERT_REQUEST_CANDIDATES, {
      expertRequestId,
      dryRun,
      sendInvitationEmail,
      invitationEmailSubject,
      invitationEmailBody,
      profileIds,
      state,
    });

    const data = result.addExpertRequestCandidates;

    if (!dryRun && data && data.success) {
      dispatch({
        type: EXPERT_REQUEST__ADD_CANDIDATE,
        expertRequestId,
        candidates: data.results!.map((r) => r!.candidate),
      });
    }
  };
}

export function requestAddExpertRequestCandidate(candidate: any): AppThunk<Promise<any>> {
  return async (_dispatch, _getState, { graphql }) => {
    const result = await graphql.mutate(REQUEST_ADD_EXPERT_REQUEST_CANDIDATE, candidate);

    return result.requestAddExpertRequestCandidate;
  };
}

export function updateExpertRequestCandidate(
  expertRequestId: string,
  candidate: ActionUpdateExpertRequestCandidateMutationVariables
): AppThunk<Promise<void>> {
  return async (dispatch, _getState, { graphql }) => {
    const { data } = await graphql.client.mutate({
      mutation: UPDATE_EXPERT_REQUEST_CANDIDATE,
      variables: candidate,
    });

    const updated = data!.updateExpertRequestCandidate;

    dispatch({
      type: EXPERT_REQUEST__UPDATE_CANDIDATE,
      expertRequestId,
      candidate: {
        ...candidate,
        ...updated,
      },
    });
  };
}

export function removeExpertRequestCandidate(
  expertRequestId: string,
  candidate: Candidate
): AppThunk<Promise<any>> {
  return (dispatch, _getState, { graphql }) =>
    graphql
      .mutate(
        gengql(/* GraphQL */ `
          mutation actionRemoveExpertRequestCandidate($id: String!) {
            removeExpertRequestCandidate(id: $id) {
              id
              email
            }
          }
        `),
        { id: candidate.id }
      )
      .then(() =>
        dispatch({
          type: EXPERT_REQUEST__REMOVE_CANDIDATE,
          expertRequestId,
          candidate,
        })
      );
}
